import { Group, MultiSelect, Select, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ListAdminRequestsParams } from "@/features/applications/api/types";
import {
	ALL_STATUSES,
	nextStatusFilter,
	QUEUE_STATUSES,
} from "@/features/applications/lib/queue-controls";
import type { PageLimit } from "@/shared/api/types";

type ApplicationsFiltersProps = {
	params: ListAdminRequestsParams;
	onChange: (
		patch: Partial<ListAdminRequestsParams> & { searchInput?: string },
	) => void;
};

export function ApplicationsFilters({
	params,
	onChange,
}: ApplicationsFiltersProps) {
	const { t } = useTranslation();
	const [search, setSearch] = useState(params.search ?? "");
	const [debounced] = useDebouncedValue(search, 350);

	useEffect(() => {
		setSearch(params.search ?? "");
	}, [params.search]);

	useEffect(() => {
		const current = params.search ?? "";
		const next = debounced.trim().length >= 3 ? debounced.trim() : "";
		if (next !== current) {
			onChange({ searchInput: debounced });
		}
	}, [debounced, onChange, params.search]);

	const statusValue = params.status?.length ? params.status : [ALL_STATUSES];

	return (
		<Group grow preventGrowOverflow={false} wrap="wrap">
			<TextInput
				label={t("common.search")}
				placeholder={t("applications.searchPlaceholder")}
				value={search}
				onChange={(event) => setSearch(event.currentTarget.value)}
			/>
			<MultiSelect
				label={t("common.status")}
				data={[
					{ value: ALL_STATUSES, label: t("applications.allStatuses") },
					...QUEUE_STATUSES.map((status) => ({
						value: status,
						label: t(`status.${status}`),
					})),
				]}
				value={statusValue}
				onChange={(value) => {
					onChange({ status: nextStatusFilter(params.status, value) });
				}}
			/>
			<Select
				label={t("common.rows")}
				data={[
					{ value: "20", label: "20" },
					{ value: "50", label: "50" },
					{ value: "100", label: "100" },
				]}
				value={String(params.limit)}
				onChange={(value) => {
					if (!value) {
						return;
					}
					onChange({ limit: Number(value) as PageLimit });
				}}
			/>
		</Group>
	);
}
